import {
  OFFLINE_CACHE_KEYS,
  readOfflineTasks,
  writeOfflineCachePayload,
} from '@/lib/offline-cache-read';
import {
  getNetworkOnline,
  isNetworkStatusInitialized,
  subscribeNetworkStatus,
} from '@/lib/network-status';
import { isTokenStorageAuthClient } from '@/lib/session-health';
import { getStoredAccessToken, safeStorageGet, safeStorageRemove, safeStorageSet } from '@/lib/storage';

export interface OfflineQueueItem {
  id: string;
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: string;
  created_at: string;
  retry_count: number;
  resource: string;
  processor?: 'http' | 'task-completion';
}

type OfflineSnapshot = {
  queue: OfflineQueueItem[];
  isProcessing: boolean;
  isOnline: boolean;
};

export type OfflineQueueDisposition = 'success' | 'drop' | 'retry';

export const OFFLINE_QUEUE_STORAGE_KEY = 'offline-queue';
export const OFFLINE_DEAD_QUEUE_STORAGE_KEY = 'offline-queue-dead';
export const OFFLINE_QUEUE_TTL_MS = 24 * 60 * 60 * 1000;
export const OFFLINE_QUEUE_MAX_RETRIES = 5;
export const OFFLINE_DEAD_QUEUE_MAX = 20;

/** 4xx except 408/429 are hopeless — drop without further retries. */
export function isHopelessOfflineHttpStatus(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

export function classifyOfflineHttpStatus(status: number): OfflineQueueDisposition {
  if (status >= 200 && status < 300) return 'success';
  if (isHopelessOfflineHttpStatus(status)) return 'drop';
  return 'retry';
}

export function isOfflineQueueItemExpired(
  createdAt: string,
  nowMs = Date.now(),
  ttlMs = OFFLINE_QUEUE_TTL_MS,
): boolean {
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return true;
  return nowMs - created > ttlMs;
}

export function pruneExpiredOfflineQueueItems(
  items: OfflineQueueItem[],
  nowMs = Date.now(),
): { kept: OfflineQueueItem[]; dropped: OfflineQueueItem[] } {
  const kept: OfflineQueueItem[] = [];
  const dropped: OfflineQueueItem[] = [];
  for (const item of items) {
    if (isOfflineQueueItemExpired(item.created_at, nowMs)) {
      dropped.push(item);
    } else {
      kept.push(item);
    }
  }
  return { kept, dropped };
}

function stripStaleAuthorization(headers: Record<string, string> | undefined): Record<string, string> {
  if (!headers) return {};
  const next = { ...headers };
  delete next.Authorization;
  delete next.authorization;
  return next;
}

function buildHttpQueueHeaders(
  request: OfflineQueueItem,
  isWebCookieClient: boolean,
): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Client': isWebCookieClient ? 'web' : 'capacitor',
    ...stripStaleAuthorization(request.headers),
  };
  if (!isWebCookieClient) {
    const token = getStoredAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

class OfflineSync {
  private queue: OfflineQueueItem[] = [];
  private isProcessing = false;
  private listeners = new Set<(snapshot: OfflineSnapshot) => void>();

  constructor() {
    this.restoreQueue();
  }

  private restoreQueue() {
    if (typeof window === 'undefined') return;
    try {
      const raw = safeStorageGet(OFFLINE_QUEUE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      const loaded = Array.isArray(parsed) ? parsed : [];
      const { kept, dropped } = pruneExpiredOfflineQueueItems(loaded);
      this.queue = kept;
      if (dropped.length > 0) {
        this.archiveDeadQueueItems(dropped, 'ttl_on_restore');
        this.persistQueue();
      }
    } catch {
      this.queue = [];
      safeStorageRemove(OFFLINE_QUEUE_STORAGE_KEY);
    }
  }

  private persistQueue() {
    if (typeof window !== 'undefined') {
      safeStorageSet(OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(this.queue));
    }
    this.emit();
  }

  private archiveDeadQueueItems(items: OfflineQueueItem[], reason: string) {
    if (typeof window === 'undefined' || items.length === 0) return;
    try {
      const raw = safeStorageGet(OFFLINE_DEAD_QUEUE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      const existing = Array.isArray(parsed) ? parsed : [];
      const stamped = items.map((item) => ({ ...item, drop_reason: reason, dropped_at: new Date().toISOString() }));
      const merged = [...stamped, ...existing].slice(0, OFFLINE_DEAD_QUEUE_MAX);
      safeStorageSet(OFFLINE_DEAD_QUEUE_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      safeStorageRemove(OFFLINE_DEAD_QUEUE_STORAGE_KEY);
    }
  }

  private dropQueueHead(reason: string) {
    const dropped = this.queue.shift();
    if (dropped) {
      this.archiveDeadQueueItems([dropped], reason);
    }
    this.persistQueue();
  }

  private emit() {
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  private resolveResource(url: string) {
    if (url.includes('/tasks')) return 'Задачи';
    if (url.includes('/incidents')) return 'Инциденты';
    if (url.includes('/consumables')) return 'Снабжение';
    if (url.includes('/checklists')) return 'Чек-листы';
    if (url.includes('/chat')) return 'Чат';
    if (url.includes('/shifts')) return 'Смены';
    if (url.includes('/absences')) return 'Отпуска';
    if (url.includes('/documents')) return 'Документы';
    return 'Данные';
  }

  private normalizeRequest(request: any): OfflineQueueItem {
    const url = String(request?.url || '');
    return {
      id: request?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      method: String(request?.method || 'POST').toUpperCase(),
      url,
      headers: request?.headers || undefined,
      body: request?.body || undefined,
      created_at: request?.created_at || new Date().toISOString(),
      retry_count: Number(request?.retry_count || 0),
      resource: request?.resource || this.resolveResource(url),
      processor: request?.processor || 'http',
    };
  }

  private async processHttpQueueItem(request: OfflineQueueItem): Promise<OfflineQueueDisposition> {
    const isWebCookieClient = !isTokenStorageAuthClient();
    const response = await fetch(request.url, {
      method: request.method,
      headers: buildHttpQueueHeaders(request, isWebCookieClient),
      body: request.body,
      credentials: isWebCookieClient ? 'include' : 'same-origin',
    });

    return classifyOfflineHttpStatus(response.status);
  }

  private async processTaskCompletionQueueItem(request: OfflineQueueItem): Promise<OfflineQueueDisposition> {
    const taskMatch = request.url.match(/\/api\/v1\/tasks\/(\d+)\/complete(?:\?|$)/i);
    if (!taskMatch) {
      return 'drop';
    }

    const isWebCookieClient = !isTokenStorageAuthClient();
    const token = !isWebCookieClient ? getStoredAccessToken() : null;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Client': isWebCookieClient ? 'web' : 'capacitor',
      ...stripStaleAuthorization(request.headers),
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const payload = request.body ? JSON.parse(request.body) : {};
    let completionPhotoId = payload?.completion_photo_id;

    const photoDraft = payload?.completion_photo_draft;
    if (!completionPhotoId && photoDraft?.dataUrl) {
      const uploadResponse = await fetch(photoDraft.dataUrl);
      const uploadBlob = await uploadResponse.blob();
      const uploadFile = new File([uploadBlob], photoDraft.name || `task-${taskMatch[1]}-proof.jpg`, {
        type: photoDraft.type || uploadBlob.type || 'image/jpeg',
      });

      const { createClientUploadId, uploadPhotoWithResilience } = await import(
        '@/lib/photo-upload-resilience'
      );
      const uploaded = await uploadPhotoWithResilience(uploadFile, {
        clientUploadId: createClientUploadId(),
        uploadSurface: 'task_completion',
        skipConnectivityProbe: true,
      });
      completionPhotoId = uploaded.id;
    }

    const completionResponse = await fetch(request.url, {
      method: request.method,
      headers,
      body: JSON.stringify({
        completion_notes: payload?.completion_notes || undefined,
        completion_photo_id: completionPhotoId || undefined,
      }),
      credentials: isWebCookieClient ? 'include' : 'same-origin',
    });

    return classifyOfflineHttpStatus(completionResponse.status);
  }

  subscribe(listener: (snapshot: OfflineSnapshot) => void) {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private getSnapshot(): OfflineSnapshot {
    return {
      queue: [...this.queue],
      isProcessing: this.isProcessing,
      isOnline: this.getIsOnline(),
    };
  }

  async addToQueue(request: any) {
    const normalized = this.normalizeRequest(request);
    if (normalized.processor === 'task-completion') {
      const existingIndex = this.queue.findIndex(
        (item) =>
          item.processor === 'task-completion' &&
          item.method === normalized.method &&
          item.url === normalized.url
      );
      if (existingIndex >= 0) {
        this.queue[existingIndex] = normalized;
      } else {
        this.queue.push(normalized);
      }
    } else {
      this.queue.push(normalized);
    }
    this.persistQueue();
  }

  async processQueue() {
    if (this.isProcessing || this.queue.length === 0 || !this.getIsOnline()) return;

    this.isProcessing = true;
    this.emit();

    while (this.queue.length > 0) {
      const request = this.queue[0];

      if (isOfflineQueueItemExpired(request.created_at)) {
        this.dropQueueHead('ttl');
        continue;
      }

      try {
        const disposition =
          request.processor === 'task-completion'
            ? await this.processTaskCompletionQueueItem(request)
            : await this.processHttpQueueItem(request);

        if (disposition === 'success') {
          this.queue.shift();
          this.persistQueue();
          continue;
        }

        if (disposition === 'drop') {
          this.dropQueueHead('hopeless_http');
          continue;
        }

        request.retry_count += 1;
        if (request.retry_count >= OFFLINE_QUEUE_MAX_RETRIES) {
          this.dropQueueHead('max_retries');
          continue;
        }
        this.persistQueue();
        break;
      } catch {
        request.retry_count += 1;
        if (request.retry_count >= OFFLINE_QUEUE_MAX_RETRIES) {
          this.dropQueueHead('max_retries');
          continue;
        }
        this.persistQueue();
        break;
      }
    }

    this.isProcessing = false;
    this.emit();
  }

  getQueueLength() {
    return this.queue.length;
  }

  getPendingRequests(): OfflineQueueItem[] {
    return [...this.queue];
  }

  getPendingSummary(): Array<{ resource: string; count: number }> {
    const grouped = new Map<string, number>();
    this.queue.forEach((item) => {
      grouped.set(item.resource, (grouped.get(item.resource) || 0) + 1);
    });
    return Array.from(grouped.entries()).map(([resource, count]) => ({ resource, count }));
  }

  /** Alias for OfflineIndicator compatibility - returns pending queue count */
  async getPendingCount(): Promise<number> {
    return this.queue.length;
  }

  /** Process queued requests when back online - alias for processQueue with result shape */
  async syncPendingRequests(): Promise<{ success: number; failed: number }> {
    const initial = this.queue.length;
    await this.processQueue();
    const remaining = this.queue.length;
    return { success: initial - remaining, failed: remaining };
  }

  getIsOnline() {
    if (typeof window === 'undefined') return true;
    if (isNetworkStatusInitialized()) return getNetworkOnline();
    return window.navigator.onLine;
  }

  onStatusChange(callback: (isOnline: boolean) => void) {
    if (typeof window === 'undefined') return () => {};

    const handleOnline = () => {
      callback(true);
      this.emit();
      void this.processQueue();
    };
    const handleOffline = () => {
      callback(false);
      this.emit();
    };

    if (isNetworkStatusInitialized()) {
      return subscribeNetworkStatus((isOnlineNow) => {
        if (isOnlineNow) handleOnline();
        else handleOffline();
      });
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }

  /** Cache shifts for offline use */
  async cacheShifts(shifts: unknown[]): Promise<void> {
    if (typeof window !== 'undefined') {
      writeOfflineCachePayload(OFFLINE_CACHE_KEYS.shifts, shifts);
    }
  }

  async cacheMyShifts(shifts: unknown[]): Promise<void> {
    if (typeof window !== 'undefined') {
      writeOfflineCachePayload(OFFLINE_CACHE_KEYS.myShifts, shifts);
    }
  }

  /** Cache tasks for offline use */
  async cacheTasks(tasks: unknown[]): Promise<void> {
    if (typeof window !== 'undefined') {
      writeOfflineCachePayload(OFFLINE_CACHE_KEYS.tasks, tasks);
    }
  }

  /** Cache checklists for offline use */
  async cacheChecklists(checklists: unknown[]): Promise<void> {
    if (typeof window !== 'undefined') {
      writeOfflineCachePayload(OFFLINE_CACHE_KEYS.checklists, checklists);
    }
  }

  /** Cache checklist instances for offline use */
  async cacheChecklistInstances(instances: unknown[]): Promise<void> {
    if (typeof window !== 'undefined') {
      writeOfflineCachePayload(OFFLINE_CACHE_KEYS.checklistInstances, instances);
    }
  }

  /** Cache locations for offline use */
  async cacheLocations(locations: unknown[]): Promise<void> {
    if (typeof window !== 'undefined') {
      writeOfflineCachePayload(OFFLINE_CACHE_KEYS.locations, locations);
    }
  }
}

export { OfflineSync };
export const offlineSync = new OfflineSync();
